import { z } from "zod"
import { ServiceAction } from "./service-part-action"
import { UniversalJobCardBike } from "./universal-job-card"

/** What a mechanic's stopwatch times. See WorkTimeRecording in the API. */
export const WorkTimeRecordingKindSchema = z.enum([
  "PART_ACTION",
  "PACKAGE",
  "WASH",
])
export type WorkTimeRecordingKind = z.infer<typeof WorkTimeRecordingKindSchema>

/**
 * A work-order row, by the same keys the work order's update routes use: a
 * part task by its partId, a package task by its id and a wash task by its
 * washSnapshotId.
 */
export const WorkTimeRecordingRowSchema = z.object({
  kind: WorkTimeRecordingKindSchema,
  rowId: z.string().uuid(),
})
export type WorkTimeRecordingRow = z.infer<typeof WorkTimeRecordingRowSchema>

/** Starting a stopwatch says who does the work; the CRM preselects the logged-in Personal. */
/** The mechanic's free-text note on a recording; blank is stored as none. */
const WorkTimeRecordingCommentSchema = z
  .string()
  .trim()
  .max(500)
  .nullable()
  .transform((comment) => (comment === "" ? null : comment))

export const StartWorkTimeRecordingSchema = WorkTimeRecordingRowSchema.extend({
  mechanicId: z.string().uuid(),
  comment: WorkTimeRecordingCommentSchema.default(null),
})
export type StartWorkTimeRecording = z.infer<
  typeof StartWorkTimeRecordingSchema
>

/** Longer than a working day is a stopwatch left running, not a measurement. */
export const MAX_WORK_TIME_RECORDING_SECONDS = 8 * 60 * 60

/** A mechanic's stopwatch on one of the job's work-order rows. */
export interface WorkTimeRecording {
  id: string
  kind: WorkTimeRecordingKind
  rowId: string
  startedAt: Date
  /** Null while the stopwatch is running. */
  stoppedAt: Date | null
  /** Null while running; may differ from stoppedAt - startedAt once edited. */
  durationSeconds: number | null
  /** Who did the work. */
  mechanic: { id: string; name: string }
  comment: string | null
}

/** A mechanic correcting a stopped recording by hand. */
export const UpdateWorkTimeRecordingSchema = z.object({
  durationSeconds: z.number().int().min(0).max(MAX_WORK_TIME_RECORDING_SECONDS),
  mechanicId: z.string().uuid(),
  comment: WorkTimeRecordingCommentSchema,
})
export type UpdateWorkTimeRecording = z.infer<
  typeof UpdateWorkTimeRecordingSchema
>

export interface RecordedWorkTime {
  averageSeconds: number
  count: number
}

/**
 * The store's recorded times, averaged. Shown next to the configured minutes
 * as information only; they never change them.
 */
export interface WorkTimeRecordingStats {
  partActions: (RecordedWorkTime & { partId: string; action: ServiceAction })[]
  packages: (RecordedWorkTime & { packageId: string })[]
  washes: (RecordedWorkTime & { washId: string })[]
}

/** One stopped recording behind an average, with the job and bike it was timed on. */
export interface WorkTimeRecordingDataPoint {
  id: string
  startedAt: Date
  durationSeconds: number
  mechanic: { id: string; name: string }
  comment: string | null
  job: {
    id: string
    displayNumber: number
    bike: UniversalJobCardBike | null
  }
}
